import { createFileRoute } from "@tanstack/react-router"
import { UseCasePage } from "@/components/use-case-page"
import { publicPageHead } from "@/lib/seo"

const title = "Twitch VOD to Shorts: AI Clip Generator | intoshort"
const description =
  "Turn a public Twitch VOD into shorts with an AI Twitch clip maker. Find stream highlights, edit cuts and captions, and export vertical clips."

export const Route = createFileRoute("/tools/twitch-vod-to-shorts")({
  head: () =>
    publicPageHead({ path: "/tools/twitch-vod-to-shorts", title, description }),
  component: TwitchVodToShortsPage,
})

function TwitchVodToShortsPage() {
  return (
    <UseCasePage
      badge="Twitch VOD to Shorts"
      eyebrow="AI Twitch VOD clipper"
      title="AI Twitch clip generator for your VODs."
      lead="Find stream highlights in a completed public Twitch VOD. intoshort searches for the moments you describe and prepares vertical clips with captions. Review each suggestion, refine the cut and choose which shorts to export."
      sourceLine="Built for public Twitch VOD links and long-form review."
      proofTitle="Search a long recording without flattening it into random clips."
      proofCopy="A stream is not one continuous highlight. intoshort looks for selected kinds of moments and preserves their chronology instead of cutting the VOD into fixed intervals."
      proofPoints={[
        "Guide the search toward reactions, explanations, plays or other moments that matter for your content.",
        "Track the real processing state while the VOD is imported, transcribed, analyzed and rendered.",
        "Keep control of the final cut, subtitle treatment and export decision.",
      ]}
      stepsTitle="How to turn a Twitch VOD into shorts."
      stepsCopy="Long sources take time to process. The project remains in one place while each stage completes, so the result does not depend on keeping a browser tab open."
      steps={[
        {
          title: "Paste a public Twitch VOD",
          copy: "intoshort validates the link and retrieves the source when Twitch makes the recording publicly available.",
        },
        {
          title: "Set the editorial direction",
          copy: "Choose the desired moments, clip duration, captions and output format before starting the job.",
        },
        {
          title: "Wait for the actual processing stages",
          copy: "Import, transcription, alignment, selection and rendering run asynchronously. The project shows progress rather than pretending the result is instant.",
        },
        {
          title: "Judge each suggestion",
          copy: "Review context around the cut, refine the clip and export the drafts that still make sense outside the original stream.",
        },
      ]}
      limitsTitle="Streams contain moments that transcripts cannot fully explain."
      limits={[
        "The VOD must still be public and accessible. Subscriber-only, expired, restricted or unavailable recordings cannot be reliably imported.",
        "Transcript-led analysis may miss a short silent play, a visual joke or context that only exists on screen. Sampled visual information helps, but it is not complete frame-by-frame understanding.",
        "Longer sources and higher-resolution exports require more processing time. A finished draft still needs a human context check.",
      ]}
      questions={[
        {
          question: "Will intoshort clip the whole stream?",
          answer:
            "No. It selects passages that fit your instructions. It does not split every few minutes into a short, and it can return fewer strong suggestions than the maximum requested.",
        },
        {
          question: "Can it find gameplay moments with no dialogue?",
          answer:
            "It may find some through visual sampling, but short silent events are easier to miss than moments supported by speech and context. Review the source if a specific play is essential.",
        },
        {
          question: "Does the VOD need to be mine?",
          answer:
            "You need permission to download, edit and republish the footage. Public availability on Twitch does not by itself grant those rights.",
        },
      ]}
      related={[
        {
          eyebrow: "Vertical layout",
          title: "Gameplay and Facecam",
          copy: "See how intoshort handles a common streamer layout in 9:16.",
          href: "/tools/gameplay-facecam-to-shorts",
        },
        {
          eyebrow: "Published videos",
          title: "YouTube to Shorts",
          copy: "Reuse a public YouTube upload through the same review-first process.",
          href: "/tools/youtube-to-shorts",
        },
      ]}
    />
  )
}
